import {Divider} from '@/components/ui/divider/Divider'
import {PageCardWrapper, PageContent} from '@/components/ui/page-atoms/PageAtoms'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {MissingDataErrorBoundary} from '@/features/projects/components/missing-data-error-boundary/MissingDataErrorBoundary'
import {ProjectFormHeading} from '@/features/projects/components/project-form/project-form-heading/ProjectFormHeading'
import {RenewProjectConfirmationDialog} from '@/features/projects/components/project-form/renew-project-confirmation-dialog/RenewProjectConfirmationDialog'
import {RenewProjectFormProvider} from '@/features/projects/components/project-form/renew-project-form-provider/RenewProjectFormProvider'
import {RenewProjectFormSections} from '@/features/projects/components/project-form/renew-project-form-sections/RenewProjectFormSections'
import {RenewProjectFormValidationSchema} from '@/features/projects/formSchemas'
import {useGetHolidays} from '@/features/projects/queries/useGetHolidays'
import {ProjectExtended} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {ErrorBoundary} from 'react-error-boundary'
import {Helmet} from 'react-helmet'
import {useOutletContext} from 'react-router-dom'
import {usePermissions} from '@/features/auth/store/store'
import {NoPermissionBox} from '@/components/commons/no-permission-box/NoPermissionBox'
import {useDialogStore} from '@ariakit/react'
import {useState} from 'react'

export const ProjectRenew = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const {project} = useOutletContext<{project: ProjectExtended}>()
    const {data: holidays} = useGetHolidays({urlParams: {year: dayjs(project.startDate).year()}})

    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
    const confirmationDialogStore = useDialogStore({
        open: isConfirmationDialogOpen,
        setOpen: setIsConfirmationDialogOpen
    })

    if (!permissions?.projects.write) {
        return <NoPermissionBox />
    }

    return (
        <>
            <Helmet title={t('projects:renew_meta_title')} />
            <ErrorBoundary fallbackRender={props => <MissingDataErrorBoundary props={props} />}>
                <PageCardWrapper>
                    <RenewProjectFormProvider project={project} holidays={holidays || []}>
                        <ProjectFormHeading
                            title={t('projects:renew_project_title')}
                            schema={RenewProjectFormValidationSchema}
                            showConfirmationModal={confirmationDialogStore.show}
                        />
                        <Divider direction="horizontal" />
                        <PageContent padding={0}>
                            <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
                                <RenewProjectFormSections isRenewalAvailable={project.isRenewable} />
                            </ScrollArea>
                        </PageContent>
                        {isConfirmationDialogOpen && (
                            <RenewProjectConfirmationDialog
                                projectId={project.id}
                                dialogStore={confirmationDialogStore}
                            />
                        )}
                    </RenewProjectFormProvider>
                </PageCardWrapper>
            </ErrorBoundary>
        </>
    )
}
